import {axiosInstance} from './api';

/**
 * Raw per-interaction LLM payload (Interaction.model_dump() in
 * services/plugins/speech_to_text/analysis.py). Internal-only: the serializer
 * strips this for non-staff, so it may be absent. Field names + semantics mirror
 * the backend pydantic schema 1:1 — keep them in sync.
 */
/**
 * One line of the reconstructed dialogue. `t` is seconds from the START OF THE
 * CONVERSATION (which begins mid-clip — see `player_offset_sec`), computed on
 * the backend: the model reports «clip N, second M inside it» and never does
 * the addition itself, because it got it wrong almost every time.
 */
export interface DialogueLine {
  t: number;
  /** 'event' — не реплика, а событие записи (писк терминала), вставленное кодом.
   *  'pause' — заметная пауза в разговоре; text — пояснение судьи, чем заняты люди. */
  speaker: 'cashier' | 'guest' | 'event' | 'pause';
  /** true = the model filled this line in; it was not heard. Rendered grey. */
  guessed: boolean;
  /** true = the line IS in the transcript but only half made out — part of the
   *  words were reconstructed. Rendered between «heard» and «guessed». */
  partial?: boolean;
  /** true = реплика из клипа, отданного предыдущему гостю: судья её видел и по ней
   *  оценивал, но в записи этого разговора её звука нет (#1396). t = 0, без дорожки. */
  prior?: boolean;
  text: string;
  clip_id: number;
  /** Вид события, когда speaker === 'event'. */
  event?: string | null;
  hz?: number | null;
  /** Сколько звучала реплика, секунды — по этому на дорожке рисуется отрезок. */
  dur?: number | null;
  /** The transcript line this grew from. Null for a guessed line: it was never
   *  in the transcript, and showing a neighbour's text instead would be a lie. */
  source: TranscriptLine | null;
  /** Все строки расшифровки, из которых собрана реплика, если их больше одной. */
  sources?: TranscriptLine[];
}

/**
 * One line of the transcript AS RECOGNISED, in our own format: time from the
 * conversation start, the pause before it, and the numbers the judge used to
 * decide who is speaking. A `tone` line is not speech at all — it is the payment
 * terminal's beep, the most reliable boundary between guests we have.
 */
export interface TranscriptLine {
  text: string;
  /** Seconds from the conversation start (absent on the per-line `source`). */
  t?: number;
  /** Start inside its own clip, seconds (only on the per-line `source`). */
  at?: number;
  dur?: number;
  /** Silence before this line, seconds. Null on the very first one. */
  pause?: number | null;
  /** Loudness over room noise, dB. The cashier stands at the mic and is louder. */
  db?: number | null;
  /** Pitch, Hz (HPS). */
  hz?: number | null;
  /** Pitch from sound islands (YIN) — the one shown; `hz` (HPS) is kept only for old rows. */
  hz2?: number | null;
  /** Two voices met head-on inside this line (from sound islands). */
  overlap?: boolean | null;
  /** Timbre: how much louder the top is than the bottom, dB. */
  timbre?: number | null;
  /** Formants — what separates two voices of the same gender. */
  f1?: number | null;
  f2?: number | null;
  /** Voice cluster label (A, B, …) — the same one the judge saw. A hint, not a fact. */
  voice?: string | null;
  // Почему строка начата здесь — та же причина разреза, что видит судья
  // («пауза 0.6с», «встык, сменился голос», «конец предложения»).
  cut?: string | null;
  /** Строка кончается знаком: «вопрос» или «конец предложения» (с 14.09). */
  ends?: string | null;
  /** «похоже на предыдущую» / «не похоже на предыдущую, неуверенно» — как у судьи. */
  rel?: string | null;
  /** Строка не громче порога «плохо слышно» — как у судьи. */
  quiet?: boolean;
  /** Non-speech event marker, e.g. «оплата». */
  tone?: string | null;
  /** What fills a gap between utterances: «тишина», «шум» or «речь» (unrecognised). */
  gap?: string | null;
  /** Voice components inside one utterance when it clearly holds more than one voice. */
  parts?: { text?: string | null; db?: number | null; hz?: number | null }[] | null;
  hz_tone?: number | null;
  clip_id?: number;
}

export interface ConversationRawLlmResponse {
  /** Step-by-step reconstructed dialogue (LLM chain-of-thought artifact). */
  reconstructed_dialogue: string;
  /** Structured form of the same dialogue — drives the player-linked view. */
  dialogue_lines?: DialogueLine[];
  /** The transcript as recognised, per line, with pauses and sound. */
  transcript_lines?: TranscriptLine[];
  /** Gap between the player's zero (first clip's start) and the conversation's. */
  player_offset_sec?: number;
  /** Where the track ENDS (seconds from the player's zero): last line plus padding.
   *  Without it the last clip played out to its end, half a minute after the guest left. */
  player_end_sec?: number | null;
  /** How many green ticks and red crosses the conversation earned. */
  /** v=2 — в bad уже учтён отсутствующий этап «Апселл». */
  marks?: { good: number; bad: number; v?: number };
  verdict?: string;

  // --- Чек-лист ПО ЭТАПАМ разговора (1:1 с analysis.py Assessment) ---
  // 1. Приветствие
  greeting: boolean;
  greeting_informal: boolean;
  // 2. Приём заказа
  asked_volume: boolean;
  client_named_volume: boolean;
  asked_here_or_togo: boolean;
  offered_ceramic: boolean;
  repeated_order: boolean;
  warned_wait_before_pay: boolean;
  asked_hot_cold: boolean;
  client_asked_togo: boolean;
  client_own_cup: boolean;
  own_cup_fulfilled: boolean;
  // 3. Доп. товар
  upsell_offered: boolean;
  upsell_filter_instead_americano: boolean;
  upsell_item?: string;
  upsell_complementary?: boolean;
  upsell_seasonal?: boolean;
  upsell_margin?: boolean;
  upsell_daypart?: boolean;
  /** Только общий вопрос «Что ещё?» без товара — слабый апселл (с 14.09). */
  upsell_open_question?: boolean;
  // Виды предложения с 15.09 (владелец): слабые — «что-то ещё?» и добавки,
  // конкретные — еда к напитку и позиция интереснее. В старых записях их нет.
  upsell_anything_else?: boolean;
  upsell_addons?: boolean;
  upsell_addons_syrup?: boolean;
  upsell_food?: boolean;
  upsell_food_items?: string;
  upsell_better_choice?: boolean;
  upsell_better_items?: string;
  /** Каждая попытка апселла отдельно (с 16.09, AB7): вид, категория без товара,
   *  товары, сироп у добавок, согласие гостя. Флаги выше — сводка по ним. */
  upsell_attempts?: UpsellAttempt[];
  /** strong — конкретный апселл, weak — только слабые виды. Считает бэкенд. */
  upsell_strength?: 'strong' | 'weak' | null;
  upsell_accepted: boolean;
  upsell_no_chance: boolean;
  // 4. Программа лояльности
  asked_loyalty: boolean;
  named_license_surfer: boolean;
  /** Как кассир назвал программу, дословно («бонусная карта»), с 16.09. */
  loyalty_phrase?: string;
  offered_loyalty_signup: boolean;
  client_has_loyalty: boolean;
  /** Гость сам показал программу; у разборов до 17.09 поля нет. */
  guest_showed_loyalty?: boolean;
  client_agreed_signup: boolean;
  loyalty_no_chance: boolean;
  // 5. Оплата
  named_total: boolean;
  payment_taken: boolean;
  asked_receipt: boolean;
  // 6. Завершение
  positive_closing: boolean;
  told_wait_time: boolean;
  // 7. Вежливость и настроение
  active_communication: boolean;
  interesting_communication: boolean;
  friendly: boolean;
  dry_minimal: boolean;
  rude: boolean;
  address_on_vy: boolean;

  has_problem: boolean;
  /** Проблемы по видам и важности (с 15.09). */
  problems?: ConversationProblem[];
  problem_severity?: ProblemSeverity | null;
  summary: string;
}

export type ProblemSeverity = 'low' | 'medium' | 'high';

export interface UpsellAttempt {
  kind: 'anything_else' | 'addons' | 'food' | 'better_choice';
  /** Реплика кассира с попыткой, дословно (с 16.09) и её номер в dialogue_lines. */
  quote?: string;
  line?: number | null;
  category: string;
  items: string;
  syrup: boolean;
  guest_agreed: boolean;
}

export interface ConversationProblem {
  /** out_of_stock — нет товара (с 16.09); dialogue — в общении; product — с товаром; cafe — остальное. */
  kind: 'out_of_stock' | 'dialogue' | 'product' | 'cafe';
  severity: ProblemSeverity;
  /** Что случилось — формально, для счёта (с 16.09): нет в наличии, оборудование… */
  category?: 'out_of_stock' | 'not_sold_here' | 'equipment' | 'quality' | 'service' | 'other';
  /** Чего касается: «сироп», «блендер». */
  subject?: string;
  text: string;
  /** Где прозвучала (с 16.09): цитата и номер реплики в dialogue_lines. */
  quote?: string;
  line?: number | null;
  /** Что просил гость и что ответил сотрудник. */
  requested?: string;
  answer?: string;
}

export interface ConversationQualityScore {
  greeting_score: number;
  farewell_score: number;
  politeness_score: number;
  order_confirmation_score: number;
  /** -1 = no natural opening to upsell in this conversation (n/a, not a miss). */
  upselling_score: number;
  /** -1 = no natural opening to mention loyalty in this conversation (n/a, not a miss). */
  loyalty_mention_score: number;
  overall_score: number;
  has_problem: boolean;
  summary: string;
  recommendations: string;
  /** Internal-only (Linza staff): raw LLM debug payload. Absent for customers. */
  raw_llm_response?: ConversationRawLlmResponse;
  analyzed_at: string;
}

export interface ConversationSegment {
  sequence_number: number;
  transcription_text: string;
  created_at: string;
  /** URL to the ~30s recording (presigned S3), or null for pre-2026-06 segments. */
  audio_url: string | null;
}

export interface ConversationAudioClip {
  sequence_number: number;
  audio_url: string;
  /** When the clip was RECORDED — the player lays clips on the real timeline by this. */
  recorded_at?: string | null;
  /** Clip length in seconds, measured on the server from the wav itself. */
  duration?: number | null;
}

/**
 * Чем разговор подошёл под поиск — приходит только когда передан `q`.
 * Считает бэк тем же правилом, каким искал: фронт иначе угадывал слово по
 * началу и на попадании с опечаткой («капуччино» нашло «капучино») не
 * подсвечивал ничего.
 */
export interface ConversationMatch {
  /** Слова ИЗ ЭТОГО разговора, которые совпали с запросом. */
  terms: string[];
  /** Номер реплики в dialogue_lines. Null у разговоров, разобранных до
   *  10.09.2026: реплик списком у них нет, есть только сплошной текст. */
  line: number | null;
}

export interface Conversation {
  id: number;
  zone: number;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  segment_count: number;
  full_transcription: string;
  status: 'RECORDING' | 'TRANSCRIBED' | 'ANALYZED' | 'NO_AUDIO' | 'NO_SPEECH' | 'NOT_CONVERSATION' | 'PARTIAL' | 'FAILED';
  quality_score: ConversationQualityScore | null;
  /** Internal-only (Linza staff): per-segment transcripts. Absent for customers. */
  segments?: ConversationSegment[];
  /** Playable recordings (no transcript) — available to all zone members. */
  audio_clips?: ConversationAudioClip[];
  /** One glued recording of the whole conversation (clips laid out by real recording
   *  time, holes kept as silence). Null for conversations built before 10.09. */
  audio_url_full?: string | null;
  /** Чем разговор подошёл под поиск. Только при поиске, иначе null. */
  match?: ConversationMatch | null;
  /** Только сотрудникам: ручная разметка разговора (null — не размечали и не помечали). */
  markup?: { flagged: boolean; events: number; updated_at: string | null } | null;
}

/** Событие ручной разметки. Время — секунды от нуля СКЛЕЕННОЙ записи разговора. */
/** Событие звукового слоя разметки (схема v2, раздел R в TODO). Речь — на дорожке спикера:
 *  реплика, слово (reply — id его реплики), речь (неразборчиво). Фон: тишина, шум, голос, другое.
 *  Звуки: писк, скан, звон, принтер, стук. */
export interface MarkupEvent {
  id: string;
  kind: string;
  start: number;
  end: number;
  text: string;
  /** id спикера (только у речи). */
  speaker: string;
  reply?: string;
  /** Как кончилась реплика: 'фраза' | 'перебили' | 'продолжает' | ''. */
  end_kind?: string;
  comment: string;
}

/** Спикер разметки: «Спикер N» или неопознанный; пол, возраст и роль — по желанию. */
export interface MarkupSpeaker {
  id: string;
  name: string;
  unknown: boolean;
  gender: '' | 'м' | 'ж';
  age: '' | 'ребёнок' | 'подросток' | 'молодой' | 'средний' | 'старший';
  role: '' | 'кассир' | 'сотрудник' | 'гость';
}

/** Логический слой диалога — не звук: пауза (интервал + что происходит) или переход (точка). */
export interface DialogueMark {
  id: string;
  kind: 'пауза' | 'переход';
  start: number;
  end: number;
  text: string;
  from: string;
  to: string;
  sub: '' | 'смена' | 'перебивание';
}

export interface MarkupBody {
  speakers: MarkupSpeaker[];
  events: MarkupEvent[];
  dialogue: DialogueMark[];
  replies: MarkupReply[];
  comment: string;
}

/** Реплика ручной разметки — группа событий (L6). */
export interface MarkupReply {
  id: string;
  speaker: string;
  events: string[];
  text: string;
  comment: string;
}

export interface ConversationMarkup {
  conversation_id: number;
  zone_id: number;
  flagged: boolean;
  flagged_by?: string | null;
  events: MarkupEvent[];
  replies: MarkupReply[];
  speakers?: MarkupSpeaker[];
  dialogue?: DialogueMark[];
  comment: string;
  updated_by: string | null;
  updated_at: string | null;
}

export const getConversationMarkup = (id: number): Promise<ConversationMarkup> =>
  axiosInstance.get<ConversationMarkup>(`/speech_to_text/conversations/${id}/markup/`).then((r) => r.data);

export const saveConversationMarkup = (
  id: number, body: MarkupBody,
): Promise<ConversationMarkup> =>
  axiosInstance.put<ConversationMarkup>(`/speech_to_text/conversations/${id}/markup/`, body).then((r) => r.data);

export const flagConversation = (id: number, flagged: boolean): Promise<ConversationMarkup> =>
  axiosInstance.post<ConversationMarkup>(`/speech_to_text/conversations/${id}/flag/`, { flagged }).then((r) => r.data);

/** Склеенная запись байтами через API: у бакета нет CORS, и волну по прямой ссылке не построить. */
export const getConversationAudioBlob = (id: number): Promise<Blob> =>
  axiosInstance
    .get<Blob>(`/speech_to_text/conversations/${id}/audio/`, { responseType: 'blob', timeout: 120000 })
    .then((r) => r.data);

export interface ConversationsKPIs {
  total_conversations: number;
  analyzed_count: number;
  problem_count: number;
  upsell_attempts: number;
  upsell_rate: number;
  avg_overall: number | null;
}

export interface ConversationsCriteriaAvg {
  greeting: number | null;
  farewell: number | null;
  politeness: number | null;
  order_confirmation: number | null;
  upselling: number | null;
  loyalty: number | null;
}

export interface ConversationsTimeseriesPoint {
  time: string;
  avg_score: number | null;
  count: number;
}

export interface ConversationsSummary {
  from: string;
  to: string;
  granularity: 'hour' | 'day';
  kpis: ConversationsKPIs;
  criteria_avg: ConversationsCriteriaAvg;
  timeseries: ConversationsTimeseriesPoint[];
}

export const getConversationsSummary = (
  zoneId: number,
  from?: string,
  to?: string,
  granularity: 'hour' | 'day' = 'day',
): Promise<ConversationsSummary> =>
  axiosInstance
    .get<ConversationsSummary>('/speech_to_text/conversations/summary/', {
      params: { zone_id: zoneId, from, to, granularity },
    })
    .then((res) => res.data);

export interface ConversationsPage {
  count: number;
  num_pages: number;
  page: number;
  page_size: number;
  results: Conversation[];
}

/** Сводка за период (AB8): бары по параметрам разговора и проблемы. Подписи значений
 *  приходят с бэкенда из пресета — набор баров не зашит в интерфейс. */
export interface OverviewValue {
  value: string;
  label: string;
  tone: 'good' | 'weak' | 'neutral' | 'bad';
  count: number;
  /** Пояснение к значению («в том числе активное»). */
  note?: string;
  /** Самые частые формулировки для подсказки: [текст, сколько раз]. */
  examples: [string, number][];
  /** Заголовок секции формулировок («что предлагали»); пусто — общий. */
  examples_title?: string;
  /** Примеры со звуком: свежие разговоры этого значения. */
  clips: OverviewClip[];
}

/** Пример со звуком (AC3): реплика и соседи, отрезок на дорожке файла разговора. */
export interface OverviewClip {
  conversation_id: number;
  started_at: string | null;
  /** Сдвиг нуля реплик относительно начала файла, с. */
  offset: number;
  /** Номер найденной реплики; null — цитаты нет, пример — начало разговора. */
  line: number | null;
  t: number;
  dur: number;
  summary: string;
  lines: { speaker: 'cashier' | 'guest'; text: string; hit: boolean }[];
}

export interface OverviewBar {
  id: string;
  title: string;
  total: number;
  no_data: number;
  values: OverviewValue[];
}

export interface OverviewProblem {
  conversation_id: number;
  started_at: string | null;
  severity: ProblemSeverity | null;
  kind: string | null;
  category: string | null;
  subject: string;
  text: string;
  requested: string;
  answer: string;
  clip: OverviewClip | null;
}

export interface ConversationsOverview {
  analyzed: number;
  bars: OverviewBar[];
  problems: {
    total: number;
    kinds: { value: string; label: string; count: number; subjects: [string, number][] }[];
    items: OverviewProblem[];
  };
}

export const getConversationsOverview = (zoneId: number, from?: string, to?: string): Promise<ConversationsOverview> =>
  axiosInstance
    .get<ConversationsOverview>('/speech_to_text/conversations/overview/', { params: { zone_id: zoneId, from, to } })
    .then((r) => r.data);

export const getConversations = (
  zoneId: number,
  from?: string,
  to?: string,
  page = 1,
  opts?: { allStatuses?: boolean; withPartial?: boolean; q?: string; flagged?: boolean; ids?: number[] },
): Promise<ConversationsPage> =>
  axiosInstance
    .get<ConversationsPage>('/speech_to_text/conversations/', {
      // `all_statuses` is honoured by the backend only for Linza staff — it
      // returns RECORDING/TRANSCRIBED/FAILED rows too (no quality_score
      // required), for the internal raw-data section.
      params: {
        zone_id: zoneId, from, to, page,
        all_statuses: opts?.allStatuses ? 'true' : undefined,
        // Обслуживания, записанные кусками: у них есть диалог и нет чек-листа.
        // Только для сотрудников Linza — клиенту показывать нечего.
        with_partial: opts?.withPartial ? 'true' : undefined,
        // Поиск по ВОССТАНОВЛЕННОМУ ллм тексту разговора, в пределах уже
        // выбранного периода. Пустая строка не передаётся — иначе бы список
        // перезапрашивался на каждое очищение поля.
        q: opts?.q?.trim() ? opts.q.trim() : undefined,
        // Очередь разметки: только помеченные сотрудником (L7/L8).
        flagged: opts?.flagged ? 'true' : undefined,
        // Разговоры под своей рамкой из «Сегментов» (L7) — по id, только сотрудникам.
        ids: opts?.ids?.length ? opts.ids.join(',') : undefined,
      },
    })
    .then((res) => res.data);

/** One ASR utterance within a clip (seconds relative to the clip start). */
export interface AudioUtterance {
  start: number;
  end: number;
  text: string;
  /** Loudness over room noise, dB — the cashier stands at the mic and is louder. */
  db?: number | null;
  /** Pitch, Hz (HPS). */
  hz?: number | null;
  /** Pitch from sound islands (YIN) — the one shown; `hz` (HPS) is kept only for old rows. */
  hz2?: number | null;
  /** Two voices met head-on inside this line (from sound islands). */
  overlap?: boolean | null;
  /** Timbre: how much louder the top is than the bottom, dB. */
  timbre?: number | null;
  /** Formants — what separates two voices of the same gender. */
  f1?: number | null;
  f2?: number | null;
  /** Voice cluster label (A, B, …) — the same one the judge saw. A hint, not a fact. */
  voice?: string | null;
  // Почему строка начата здесь — та же причина разреза, что видит судья
  // («пауза 0.6с», «встык, сменился голос», «конец предложения»).
  cut?: string | null;
  /** Строка кончается знаком: «вопрос» или «конец предложения» (с 14.09). */
  ends?: string | null;
  /** Non-speech event, e.g. «оплата» — the payment terminal's beep. */
  tone?: string | null;
  /** What fills a gap between utterances: «тишина», «шум» or «речь». */
  gap?: string | null;
  /** Voice components inside one utterance when it clearly holds more than one voice. */
  parts?: { text?: string | null; db?: number | null; hz?: number | null }[] | null;
}

/** Raw captured clip (AudioRecord) for the Linza-staff raw-data section. */
export interface AudioRecordRaw {
  id: number;
  zone: number;
  /** Conversation this clip landed in, or null (no-speech / non-customer / gap). */
  conversation: number | null;
  sequence_number: number;
  status: 'PENDING' | 'NO_SPEECH' | 'TRANSCRIBED' | 'ANALYZED' | 'NOT_CONVERSATION' | 'PARTIAL' | 'FAILED';
  transcription_text: string;
  transcript_segments: AudioUtterance[];
  clip_duration: number;
  /** When the clip was RECORDED (start of capture). Null for clips from before 10.09.2026. */
  recorded_at?: string | null;
  created_at: string;
  /** Presigned S3 URL, or null for pre-2026-06 clips whose audio was deleted. */
  audio_url: string | null;
  /** Текст клипа, исправленный человеком (строка пула AudioLabel); только в потоке «Сегментов». */
  label?: ClipLabel | null;
}

/** corrected_text: null — не слушали, "" — шум/неразборчиво, текст — дословно. */
export interface ClipLabel {
  corrected_text: string | null;
  source: 'user' | 'ai' | null;
  annotated_by: string | null;
  annotated_at: string | null;
}

export const saveClipLabel = (recId: number, correctedText: string | null): Promise<ClipLabel | null> =>
  axiosInstance
    .post<ClipLabel | null>(`/speech_to_text/audio-records/${recId}/label/`, { corrected_text: correctedText })
    .then((res) => res.data);

export interface AudioRecordsPage {
  count: number;
  num_pages: number;
  page: number;
  page_size: number;
  results: AudioRecordRaw[];
}

export const getAudioRecords = (
  zoneId: number,
  from?: string,
  to?: string,
  page = 1,
): Promise<AudioRecordsPage> =>
  axiosInstance
    .get<AudioRecordsPage>('/speech_to_text/audio-records/', {
      params: { zone_id: zoneId, from, to, page },
    })
    .then((res) => res.data);


/** Поток клипов по времени записи — для «Данные → Аудио → Сегменты» (L3), только сотрудникам. */
export const getAudioRecordsStream = (
  zoneId: number, since: string, until: string, page = 1, pageSize = 200,
): Promise<AudioRecordsPage> =>
  axiosInstance
    .get<AudioRecordsPage>('/speech_to_text/audio-records/', {
      params: { zone_id: zoneId, stream: 'true', since, until, page, page_size: pageSize },
    })
    .then((res) => res.data);

/** Ручная рамка диалога в потоке клипов. start/end — ISO UTC. */
export interface DialogueBoundary {
  id: string;
  start: string;
  end: string;
  color: string;
  comment: string;
  conversation_id: number | null;
  /** Копия прод-разговора, который человек убрал совсем (T2). */
  deleted?: boolean;
}

export interface DialogueBoundaries {
  zone_id: number;
  day: string;
  items: DialogueBoundary[];
  updated_by: string | null;
  updated_at: string | null;
}

export const getDialogueBoundaries = (zoneId: number, day: string): Promise<DialogueBoundaries> =>
  axiosInstance.get<DialogueBoundaries>('/speech_to_text/markup/boundaries/', { params: { zone_id: zoneId, day } })
    .then((r) => r.data);

export const saveDialogueBoundaries = (zoneId: number, day: string, items: DialogueBoundary[]): Promise<DialogueBoundaries> =>
  axiosInstance.put<DialogueBoundaries>('/speech_to_text/markup/boundaries/', { zone_id: zoneId, day, items })
    .then((r) => r.data);

/** Байты одного клипа через API — для пересборки файла по сегментам в браузере (L7). */
export const getClipAudioBlob = (recId: number): Promise<Blob> =>
  axiosInstance
    .get<Blob>(`/speech_to_text/audio-records/${recId}/audio/`, { responseType: 'blob', timeout: 60000 })
    .then((r) => r.data);

/** Разметка внутри своей рамки из «Сегментов». Время событий — секунды от начала рамки. */
export interface BoundaryMarkup {
  item_id: string;
  events: MarkupEvent[];
  replies: MarkupReply[];
  speakers?: MarkupSpeaker[];
  dialogue?: DialogueMark[];
  comment: string;
  updated_by: string | null;
  updated_at: string | null;
  flagged: boolean;
}

export const getBoundaryMarkup = (zoneId: number, day: string, itemId: string): Promise<BoundaryMarkup> =>
  axiosInstance.get<BoundaryMarkup>('/speech_to_text/markup/boundaries/item/', { params: { zone_id: zoneId, day, item_id: itemId } })
    .then((r) => r.data);

export const saveBoundaryMarkup = (
  zoneId: number, day: string, itemId: string, body: MarkupBody,
): Promise<BoundaryMarkup> =>
  axiosInstance.put<BoundaryMarkup>('/speech_to_text/markup/boundaries/item/', { zone_id: zoneId, day, item_id: itemId, ...body })
    .then((r) => r.data);
